import React from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet, CreditCard, ArrowLeft, Info, CheckCircle2, ShieldCheck, ArrowRight, Building2,
} from 'lucide-react';

/**
 * Public fee schedule — explains the 3 transaction scenarios in plain LV.
 * Linked from Terms of Use, the cart checkout breakdown, and the wallet's
 * StripeConnectCard so buyers/sellers can always reach the source-of-truth
 * pricing page without contacting support.
 */
export default function FeeSchedulePage() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="fee-schedule-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#121212] mb-6"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" /> Atpakaļ uz sākumu
        </Link>

        {/* Hero */}
        <div className="mb-10">
          <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-[#121212] mb-3">
            Maksas un komisijas
          </h1>
          <p className="text-base sm:text-lg text-[#5C5C5C] max-w-2xl">
            Caurspīdīgs cenrādis pircējiem un pārdevējiem. Šeit ir visi 3 pamatdarījumu scenāriji
            ar precīziem aprēķiniem.
          </p>
        </div>

        {/* Seller promise — clear summary so sellers see exactly what THEY pay */}
        <section className="mb-10 bg-emerald-50 border border-emerald-200 rounded-2xl p-6 sm:p-8" data-testid="seller-promise">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-outfit text-xl sm:text-2xl font-semibold text-[#121212] mb-2">
                Pārdevējam — tikai 10% komisija
              </h2>
              <p className="text-sm text-[#5C5C5C] mb-3">
                Kā pārdevējs Tu maksā <strong className="text-[#121212]">tikai 10% komisiju</strong> no pārdotās
                preces vai pakalpojuma cenas. Nav ikmēneša maksas, nav reģistrācijas maksas, nav slēptu izmaksu.
              </p>
              <ul className="space-y-2 text-sm text-[#5C5C5C]">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span><strong className="text-[#121212]">€0.40 servisa maksu sedz pircējs</strong>, nevis Tu — tā nosedz Stripe kartes apstrādi un tiek piemērota tikai tad, kad pircējs maksā ar karti. Tev komisija paliek 10%.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span><strong className="text-[#121212]">Pirmā izmaksa katru mēnesi ir bezmaksas.</strong> €0.50 apkalpošanas maksa attiecas tikai uz 2. un nākamajām izmaksām uz banku tajā pašā mēnesī — to var izvairīties, izmaksājot vienreiz mēnesī.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <span>Maksājumos no KoLV maciņa (iekšēji) nav nekādu Stripe maksu — pārdevējs saņem pilnus 90%.</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Scenario 1: Stripe card purchase */}
        <section className="mb-8 bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8" data-testid="scenario-stripe">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#F5F4FF] flex items-center justify-center shrink-0">
              <CreditCard className="w-6 h-6 text-[#635BFF]" />
            </div>
            <div>
              <h2 className="font-outfit text-xl sm:text-2xl font-semibold text-[#121212] mb-1">
                1. Pirkums ar karti (Stripe)
              </h2>
              <p className="text-sm text-[#5C5C5C]">
                Kad pircējs maksā ar maksājumu karti caur Stripe.
              </p>
            </div>
          </div>

          <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-4 sm:p-5 mb-4">
            <div className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wide mb-2">Piemērs ar €20 preci</div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[#E5E4E0]">
                <tr><td className="py-2 pr-3">Preces cena</td><td className="py-2 text-right font-medium">€20.00</td></tr>
                <tr><td className="py-2 pr-3">+ Platformas servisa maksa <span className="text-xs text-[#5C5C5C]">(pircēja pusē)</span></td><td className="py-2 text-right font-medium">€0.40</td></tr>
                <tr className="bg-white"><td className="py-2 pr-3 font-bold">= Pircējs maksā kasē</td><td className="py-2 text-right font-bold text-[#121212]">€20.40</td></tr>
                <tr><td className="py-2 pr-3">Stripe apstrādes maksa <span className="text-xs text-[#5C5C5C]">(KoLV puses)</span></td><td className="py-2 text-right text-[#5C5C5C]">−€0.54</td></tr>
                <tr><td className="py-2 pr-3">KoLV 10% komisija <span className="text-xs text-[#5C5C5C]">(pārdevēja pusē)</span></td><td className="py-2 text-right text-[#5C5C5C]">−€2.00</td></tr>
                <tr className="bg-emerald-50"><td className="py-2 pr-3 font-bold text-emerald-800">→ Pārdevēja maciņā</td><td className="py-2 text-right font-bold text-emerald-800">€18.00</td></tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-[#5C5C5C] shrink-0 mt-0.5" />
              <span className="text-[#5C5C5C]">
                <strong className="text-[#121212]">Pircējs sedz</strong> €0.40 fiksētu maksu, lai nosegtu Stripe bankas apstrādi.
              </span>
            </div>
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-[#5C5C5C] shrink-0 mt-0.5" />
              <span className="text-[#5C5C5C]">
                <strong className="text-[#121212]">Pārdevējs sedz</strong> 10% no preces vērtības (platformas uzturēšana, nodokļi).
              </span>
            </div>
          </div>
        </section>

        {/* Scenario 2: Wallet-to-wallet */}
        <section className="mb-8 bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8" data-testid="scenario-wallet">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#FFF8F0] flex items-center justify-center shrink-0">
              <Wallet className="w-6 h-6 text-[#D97736]" />
            </div>
            <div>
              <h2 className="font-outfit text-xl sm:text-2xl font-semibold text-[#121212] mb-1">
                2. Pirkums no maciņa (iekšējais)
              </h2>
              <p className="text-sm text-[#5C5C5C]">
                Kad pircējs maksā ar KoLV maciņa atlikumu — bez kartēm, bez Stripe.
              </p>
            </div>
          </div>

          <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-4 sm:p-5 mb-4">
            <div className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wide mb-2">Piemērs ar €20 preci</div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[#E5E4E0]">
                <tr><td className="py-2 pr-3">Preces cena</td><td className="py-2 text-right font-medium">€20.00</td></tr>
                <tr className="bg-white"><td className="py-2 pr-3 font-bold">Pircējs maksā no maciņa</td><td className="py-2 text-right font-bold text-[#121212]">€20.00</td></tr>
                <tr><td className="py-2 pr-3">KoLV 10% komisija</td><td className="py-2 text-right text-[#5C5C5C]">−€2.00</td></tr>
                <tr className="bg-emerald-50"><td className="py-2 pr-3 font-bold text-emerald-800">→ Pārdevēja maciņā</td><td className="py-2 text-right font-bold text-emerald-800">€18.00</td></tr>
              </tbody>
            </table>
          </div>

          <div className="flex items-start gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <span className="text-emerald-800">
              <strong>Bez Stripe maksām</strong> — nauda jau atrodas KoLV ekosistēmā, tāpēc nekādu papildu apstrādes maksu nav.
              Pārdevējam saglabājas pilna 90% no preces cenas.
            </span>
          </div>
        </section>

        {/* Scenario 3: Payouts to bank */}
        <section className="mb-8 bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8" data-testid="scenario-payout">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-outfit text-xl sm:text-2xl font-semibold text-[#121212] mb-1">
                3. Izmaksa uz banku (Stripe Connect)
              </h2>
              <p className="text-sm text-[#5C5C5C]">
                Kad pārdevējs spiež "Izmaksāt caur Stripe", lai pārceltu naudu no maciņa uz savu banku.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">
                1. izmaksa mēnesī
              </div>
              <div className="font-outfit text-3xl font-bold text-emerald-800 mb-1">€0.00</div>
              <div className="text-sm text-emerald-700">Bez maksas — Tu saņem pilnu summu uz banku.</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">
                2.+ izmaksa mēnesī
              </div>
              <div className="font-outfit text-3xl font-bold text-amber-800 mb-1">€0.50</div>
              <div className="text-sm text-amber-700">Apkalpošanas maksa, atskaitīta no maciņa pirms pārskaitījuma.</div>
            </div>
          </div>

          <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-4 sm:p-5 mb-4 text-sm">
            <div className="font-semibold text-[#121212] mb-2">Piemērs: Tu šomēnes izmaksā divreiz €15</div>
            <ul className="space-y-1 text-[#5C5C5C]">
              <li><strong>1. izmaksa:</strong> €15.00 → banka saņem €15.00 (no maciņa atskaitīts €15.00)</li>
              <li><strong>2. izmaksa:</strong> €15.00 → banka saņem €15.00 (no maciņa atskaitīts €15.50: €15 + €0.50 fee)</li>
            </ul>
          </div>

          <div className="flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 rounded-xl p-3">
            <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <span className="text-blue-800">
              <strong>Minimālā izmaksa: €5.00.</strong> Stripe nauda parasti nonāk Tavā bankā 1-2 darba dienu laikā caur SEPA.
            </span>
          </div>
        </section>

        {/* Compliance note */}
        <section className="mb-8 bg-gradient-to-br from-[#FFF8F0] to-[#FFE4D1] border border-[#FBD8B5] rounded-2xl p-6 sm:p-8" data-testid="scenario-compliance">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#D97736] flex items-center justify-center shrink-0">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-outfit text-xl sm:text-2xl font-semibold text-[#121212] mb-2">
                Privātpersonu likumiskie limiti
              </h2>
              <p className="text-sm text-[#5C5C5C] mb-3">
                Saskaņā ar DAC7 direktīvu un AML noteikumiem, privātpersonām (bez saimnieciskās darbības) ir šādi limiti:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#D97736] font-bold">•</span>
                  <span><strong>€2 000 gadā</strong> — maksimālais apgrozījums</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D97736] font-bold">•</span>
                  <span><strong>30 darījumi gadā</strong> — maksimālais skaits</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D97736] font-bold">•</span>
                  <span><strong>€250 mēnesī</strong> — maksimālā izmaksa uz banku</span>
                </li>
              </ul>
              <p className="text-sm text-[#5C5C5C] mt-3">
                Lai pārsniegtu šos limitus, jāreģistrē saimnieciskās darbības forma (pašnodarbinātais VID EDS, Swedbank SDI konts vai SIA/IK).
              </p>
              <Link
                to="/seller/upgrade-status"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#D97736] hover:underline"
              >
                Uzzini vairāk par statusa atjaunošanu <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Footer links */}
        <div className="text-center text-sm text-[#5C5C5C]">
          <p>
            Saistīti dokumenti: {' '}
            <Link to="/lietosanas-noteikumi" className="text-[#D97736] underline">Lietošanas noteikumi</Link>
            {' · '}
            <Link to="/privatuma-politika" className="text-[#D97736] underline">Privātuma politika</Link>
            {' · '}
            <Link to="/sikdatnu-politika" className="text-[#D97736] underline">Sīkdatņu politika</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
